import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Receipt, Loader2, Search, FolderKanban, User, Send, Link2, FileDown, Pencil, CheckCircle2, Banknote, CreditCard } from "lucide-react";
import QuickInvoiceModal from "../components/QuickInvoiceModal";
import SendInvoiceDialog from "../components/SendInvoiceDialog";
import EmailStatusBadge from "../components/EmailStatusBadge";
import { useData } from "../contexts/DataContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-700",
  partial: "bg-amber-100 text-amber-700",
  sent: "bg-blue-100 text-blue-700",
  pending: "bg-slate-100 text-slate-600",
  overdue: "bg-red-100 text-red-700",
};

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Billing() {
  const { contacts, fetchContacts, projects, fetchProjects } = useData();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [sendDialog, setSendDialog] = useState({ open: false, invoiceId: "", invoiceNumber: "", defaultEmail: "" });
  const [filter, setFilter] = useState("all"); // all | standalone | project | unpaid | paid
  const [search, setSearch] = useState("");
  const [emailStatus, setEmailStatus] = useState({}); // invoice_id -> latest email record

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/invoices`);
      setInvoices(res.data || []);
    } catch {}
    setLoading(false);
  }, []);

  // Fetch latest email status for all loaded invoices in one shot
  useEffect(() => {
    if (!invoices.length) {
      setEmailStatus({});
      return;
    }
    const ids = invoices.map(i => i.id).join(",");
    axios.get(`${API_URL}/emails/status`, { params: { entity_type: "invoice", entity_ids: ids } })
      .then(res => setEmailStatus(res.data?.records || {}))
      .catch(() => setEmailStatus({}));
  }, [invoices]);

  useEffect(() => {
    loadInvoices();
    fetchContacts();
    fetchProjects();
  }, [loadInvoices, fetchContacts, fetchProjects]);

  const contactMap = Object.fromEntries((contacts || []).map(c => [c.id, c]));
  const projectMap = Object.fromEntries((projects || []).map(p => [p.id, p]));

  const filteredInvoices = invoices.filter(inv => {
    const isStandalone = !inv.project_id;
    if (filter === "standalone" && !isStandalone) return false;
    if (filter === "project" && isStandalone) return false;
    if (filter === "unpaid" && inv.status === "paid") return false;
    if (filter === "paid" && inv.status !== "paid") return false;
    if (search) {
      const q = search.toLowerCase();
      const proj = projectMap[inv.project_id];
      const contactId = inv.contact_id || proj?.contact_id;
      const contact = contactMap[contactId];
      const hay = [
        inv.invoice_number, inv.title, inv.description,
        contact?.name, contact?.email, proj?.project_address,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const totals = filteredInvoices.reduce((acc, inv) => {
    const amount = parseFloat(inv.amount) || 0;
    const paid = parseFloat(inv.paid_amount) || 0;
    acc.total += amount;
    acc.paid += paid;
    acc.outstanding += Math.max(amount - paid, 0);
    return acc;
  }, { total: 0, paid: 0, outstanding: 0 });

  const openInvoice = (inv) => {
    // Open the editor directly so users can view + edit any invoice in one click,
    // regardless of project_id or paid status.
    setEditInvoice(inv);
    setShowModal(true);
  };

  const handleSend = (inv) => {
    // Resolve the default recipient — direct contact_id first, then via linked project.
    let email = "";
    if (inv.contact_id) {
      email = contacts.find(c => c.id === inv.contact_id)?.email || "";
    }
    if (!email && inv.project_id) {
      const proj = projects.find(p => p.id === inv.project_id);
      if (proj?.contact_id) {
        email = contacts.find(c => c.id === proj.contact_id)?.email || "";
      }
    }
    setSendDialog({
      open: true,
      invoiceId: inv.id,
      invoiceNumber: inv.invoice_number || "",
      defaultEmail: email,
    });
  };

  const handleCopyLink = (inv) => {
    const code = inv.short_code || inv.id;
    const link = `${window.location.origin}/i/${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Payment link copied!");
  };

  const [markPaidDialog, setMarkPaidDialog] = useState({ open: false, invoice: null, cash: false, silent: false });

  const handleMarkPaid = (inv) => {
    if (inv.status === "paid") return;
    setMarkPaidDialog({ open: true, invoice: inv, cash: false, silent: false });
  };

  const confirmMarkPaid = async () => {
    const inv = markPaidDialog.invoice;
    const { cash, silent } = markPaidDialog;
    if (!inv) return;
    try {
      await axios.post(
        `${API_URL}/invoices/${inv.id}/mark-paid?cash=${cash ? "true" : "false"}&silent=${silent ? "true" : "false"}`,
      );
      toast.success(
        silent
          ? "Correction recorded — nothing sent to the customer"
          : (cash ? "Invoice marked paid (cash price)" : "Invoice marked paid"),
      );
      setMarkPaidDialog({ open: false, invoice: null, cash: false, silent: false });
      loadInvoices();
    } catch (err) {
      console.error("[Mark Paid] failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to mark invoice paid");
    }
  };

  const handleDownloadPDF = async (inv) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `Invoice-${inv.invoice_number || inv.id}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("[Invoice PDF] Download failed:", err);
      toast.error(err?.response?.status ? `Failed to generate PDF (${err.response.status})` : "Failed to generate PDF");
    }
  };

  return (
    <div className="min-h-screen">
      <Header title="Billing">
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270] h-10"
          onClick={() => setShowModal(true)}
          data-testid="billing-new-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New Quick Invoice
        </Button>
      </Header>

      <div className="p-4 sm:p-6 space-y-4">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total Invoiced</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{fmt(totals.total)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Collected</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{fmt(totals.paid)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Outstanding</p>
              <p className="text-2xl font-bold text-amber-600 mt-1">{fmt(totals.outstanding)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search invoices, contacts, addresses…"
              className="h-10 pl-9"
              data-testid="billing-search"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-10 w-44" data-testid="billing-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All invoices</SelectItem>
              <SelectItem value="standalone">Standalone only</SelectItem>
              <SelectItem value="project">Project invoices</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
          </div>
        ) : filteredInvoices.length > 0 ? (
          <Card>
            <div className="divide-y divide-slate-100">
              {filteredInvoices.map(inv => {
                const isStandalone = !inv.project_id;
                const proj = projectMap[inv.project_id];
                const contactId = inv.contact_id || proj?.contact_id;
                const contact = contactMap[contactId];
                const paid = parseFloat(inv.paid_amount) || 0;
                const amount = parseFloat(inv.amount) || 0;
                const remaining = Math.max(amount - paid, 0);
                return (
                  <div
                    key={inv.id}
                    className="group flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-3 sm:px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => openInvoice(inv)}
                    data-testid={`billing-row-${inv.id}`}
                  >
                    <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                      <Receipt className={`w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0 ${isStandalone ? "text-amber-600" : "text-[#0F4C81]"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800">{inv.invoice_number || "Draft"}</span>
                          {isStandalone ? (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px] border-0">Quick</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] capitalize">{inv.invoice_type}</Badge>
                          )}
                          {inv.title && <span className="text-sm text-slate-500 truncate">{inv.title}</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 flex-wrap">
                          {contact && (
                            <span className="flex items-center gap-1"><User className="w-3 h-3" />{contact.name}</span>
                          )}
                          {proj?.project_address && (
                            <span className="flex items-center gap-1 truncate max-w-full"><FolderKanban className="w-3 h-3 flex-shrink-0" /><span className="truncate">{proj.project_address}</span></span>
                          )}
                          <span>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ""}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3 pl-8 sm:pl-0">
                      <div className="text-left sm:text-right">
                        <p className="font-bold text-slate-800">{fmt(amount)}</p>
                        {paid > 0 && paid < amount && (
                          <p className="text-[11px] text-amber-600">{fmt(remaining)} due</p>
                        )}
                      </div>
                      <Badge className={`${STATUS_STYLES[inv.status] || STATUS_STYLES.pending} text-[10px] capitalize border-0`}>
                        {inv.status}
                      </Badge>
                      <EmailStatusBadge record={emailStatus[inv.id]} />
                      <div className="flex items-center gap-1 sm:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => { setEditInvoice(inv); setShowModal(true); }}
                        className="p-1.5 rounded hover:bg-[#0F4C81]/10 text-[#0F4C81]"
                        title="Edit invoice"
                        data-testid={`billing-edit-${inv.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {inv.status !== "paid" && (
                        <button
                          type="button"
                          onClick={() => handleMarkPaid(inv)}
                          className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
                          title="Mark invoice paid (no Payment record)"
                          data-testid={`billing-markpaid-${inv.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {contact?.email && (
                        <button
                          type="button"
                          onClick={() => handleSend(inv)}
                          className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                          title={`Email invoice to ${contact.email}`}
                          data-testid={`billing-send-${inv.id}`}
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleCopyLink(inv)}
                        className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600"
                        title="Copy payment link"
                        data-testid={`billing-copylink-${inv.id}`}
                      >
                        <Link2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadPDF(inv)}
                        className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
                        title="Download PDF"
                        data-testid={`billing-pdf-${inv.id}`}
                      >
                        <FileDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ) : (
          <Card>
            <CardContent className="text-center py-16 text-slate-400">
              <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p>No invoices match your filters</p>
              <Button
                variant="link"
                className="text-[#0F4C81] mt-2"
                onClick={() => setShowModal(true)}
              >
                Create your first quick invoice
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <QuickInvoiceModal
        open={showModal}
        onOpenChange={(v) => { setShowModal(v); if (!v) setEditInvoice(null); }}
        contacts={contacts}
        editInvoice={editInvoice}
        onCreated={() => loadInvoices()}
      />
      <SendInvoiceDialog
        open={sendDialog.open}
        onOpenChange={(v) => setSendDialog(prev => ({ ...prev, open: v }))}
        invoiceId={sendDialog.invoiceId}
        invoiceNumber={sendDialog.invoiceNumber}
        defaultEmail={sendDialog.defaultEmail}
        onSent={loadInvoices}
      />

      {/* Mark Paid dialog with Cash toggle. Replaces the old window.confirm
          so admin can lock in the 2% cash discount on the invoice at the
          moment of payment. Original card total is preserved on the invoice
          as `pre_cash_amount` so the audit trail is intact. */}
      <Dialog open={markPaidDialog.open} onOpenChange={(v) => !v && setMarkPaidDialog({ open: false, invoice: null, cash: false })}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Mark invoice paid</DialogTitle>
            <DialogDescription>
              Invoice {markPaidDialog.invoice?.invoice_number || ""} · {markPaidDialog.silent
                ? "Logs an adjustment payment and updates the totals. Nothing is sent to the customer and the pipeline stage stays put."
                : "Stamped as paid without creating a Payment record. If this is the last unpaid invoice on the linked project, it will auto-move to Final Paid."}
            </DialogDescription>
          </DialogHeader>
          {markPaidDialog.invoice && (() => {
            const orig = parseFloat(markPaidDialog.invoice.amount) || 0;
            const cashAmt = Math.round(orig * 0.98 * 100) / 100;
            const discount = Math.round((orig - cashAmt) * 100) / 100;
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setMarkPaidDialog(prev => ({ ...prev, cash: false }))}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${!markPaidDialog.cash ? "border-[#0F4C81] bg-[#0F4C81]/5" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid="mark-paid-option-card"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CreditCard className={`w-4 h-4 ${!markPaidDialog.cash ? "text-[#0F4C81]" : "text-slate-400"}`} />
                      <span className={`text-xs font-semibold uppercase tracking-wider ${!markPaidDialog.cash ? "text-[#0F4C81]" : "text-slate-500"}`}>Card / Check</span>
                    </div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{fmt(orig)}</div>
                    <div className="text-xs text-slate-500 mt-1">Full invoice total</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMarkPaidDialog(prev => ({ ...prev, cash: true }))}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${markPaidDialog.cash ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid="mark-paid-option-cash"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Banknote className={`w-4 h-4 ${markPaidDialog.cash ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className={`text-xs font-semibold uppercase tracking-wider ${markPaidDialog.cash ? "text-emerald-700" : "text-slate-500"}`}>Cash</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-700 tabular-nums">{fmt(cashAmt)}</div>
                    <div className="text-xs text-emerald-600 mt-1">2% cash discount ({fmt(discount)} off)</div>
                  </button>
                </div>
                {markPaidDialog.cash && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="mark-paid-cash-notice">
                    Invoice total will update to {fmt(cashAmt)}. The card price {fmt(orig)} stays on the audit log so nothing is lost.
                  </p>
                )}
              </div>
            );
          })()}
          {/* Bookkeeping correction. Still writes totals + an `adjustment`
              payment record, but sends the customer nothing and leaves the
              pipeline stage untouched. */}
          <label
            className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 cursor-pointer"
            data-testid="mark-paid-silent-label"
          >
            <input
              type="checkbox"
              checked={!!markPaidDialog.silent}
              onChange={(e) => setMarkPaidDialog(prev => ({ ...prev, silent: e.target.checked }))}
              className="mt-0.5 h-4 w-4 accent-amber-600"
              data-testid="mark-paid-silent-checkbox"
            />
            <span className="text-sm text-amber-900">
              <span className="font-semibold">Record correction only, no customer notification</span>
              <span className="block text-xs text-amber-800 mt-0.5">
                Updates the invoice and logs an <b>adjustment</b> payment. No receipt, no pipeline stage change.
              </span>
            </span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidDialog({ open: false, invoice: null, cash: false, silent: false })} data-testid="mark-paid-cancel">Cancel</Button>
            <Button onClick={confirmMarkPaid} className={markPaidDialog.silent ? "bg-amber-600 hover:bg-amber-700" : (markPaidDialog.cash ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#0F4C81] hover:bg-[#0F4C81]/90")} data-testid="mark-paid-confirm">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {markPaidDialog.silent ? "Record correction" : `Mark paid ${markPaidDialog.cash ? "(cash)" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
